import { Quote, Star, Users, Award } from 'lucide-react';
import { motion } from 'framer-motion';

const testimonials = [
  {
    name: 'María González',
    course: 'Curso OS-10',
    text: 'Excelente instituto, docentes muy preparados y material de calidad. La certificación me abrió nuevas oportunidades laborales.',
    avatar: 'MG',
    rating: 5,
  },
  {
    name: 'Carlos Rojas',
    course: 'Supervisor de Guardia',
    text: 'La modalidad online fue perfecta para mí. Pude estudiar mientras trabajaba y el aula virtual es muy completa.',
    avatar: 'CR',
    rating: 5,
  },
  {
    name: 'Felipe Muñoz',
    course: 'Formación Guardia de Seguridad Privada',
    text: 'Excelente curso, me preparó muy bien para el examen de OS-10. Los instructores tienen gran experiencia en el rubro y el material es muy completo.',
    avatar: 'FM',
    rating: 5,
  },
];

const companies = [
  { name: 'Mundial Kenpo Karate', logo: '/assets/logoMDK.jpeg', alt: 'Mundial Kenpo Karate Valparaíso', url: 'https://mundialkenpokarate.cl/' },
];

const stats = [
  { value: '+1,000', label: 'Alumnos Capacitados', icon: Users },
  { value: '98%', label: 'Satisfacción', icon: Star },
  { value: '+5', label: 'Años de Experiencia', icon: Award },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Testimonials() {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-100/50 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-medium rounded-full mb-4">
            Testimonios
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6 font-heading">
            Lo Que Dicen{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Nuestros Alumnos
            </span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Miles de profesionales han confiado en nosotros para impulsar sus carreras
          </p>
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16 max-w-3xl mx-auto"
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                className="flex flex-col items-center p-6 rounded-2xl bg-white shadow-lg shadow-slate-200/50 border border-slate-100"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-3">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
                <p className="text-sm text-slate-500">{stat.label}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Testimonial cards */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8, scale: 1.02 }}
              className="group bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 relative overflow-hidden"
            >
              {/* Gradient accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500" />

              {/* Quote icon */}
              <div className="absolute top-6 right-6 w-12 h-12 rounded-full bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
                <Quote className="w-5 h-5 text-blue-500" />
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Content */}
              <p className="text-slate-600 leading-relaxed mb-6">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4 pt-4 border-t border-slate-100">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white flex items-center justify-center font-bold text-lg">
                  {testimonial.avatar}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800">{testimonial.name}</h4>
                  <p className="text-sm text-slate-500">{testimonial.course}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Partners */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-8">
            Certificados y Alianzas Estratégicas
          </p>
          <div className="flex flex-wrap justify-center gap-6 md:gap-10 items-center">
            {companies.map((company, index) => (
              <motion.a
                key={index}
                href={company.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="bg-white p-6 rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 w-44 h-28 flex items-center justify-center hover:grayscale-0 transition-all duration-300"
              >
                <img
                  src={company.logo}
                  alt={company.alt}
                  className="max-w-full max-h-full object-contain"
                />
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
